import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';

import { Accordion, Alert, Button, FormControl } from 'react-bootstrap';

import checkPermissions from '../../CheckPermissions';
import LegacySignals from './LegacySignals';
import { PROFILES_PERMISSIONS } from '../../../../permissions';
import SignalsRules, { SignalEditSession } from './SignalsRules';
import Store from '../../../../store';
import { parseYamlSignalsToUi, signalGroupPlaceHolder } from './utils';
import './signalGroups.css';
import { action } from 'mobx';

interface SignalsGroupComponentProps {
  store?: Store;
  saveSignals: () => void;
  /**
   * A signal to open and mark, set when one is clicked in the lead sample.
   * Carries a nonce so clicking the same signal twice reopens it: without one,
   * closing the group by hand and clicking the same line again would change
   * nothing, because the prop would be identical.
   */
  focus?: { group: number; rule: number; nonce: number } | null;
  /** Collapse each definition to one line. Only the merged Review page opts in. */
  compact?: boolean;
  /**
   * Edit one definition at a time, each with its own Save and Cancel, instead
   * of one Save button for the whole card.
   *
   * A separate prop from `compact` on purpose. `compact` describes a layout; if
   * saving semantics rode on it, the next person wanting the compact list would
   * silently get this with it, and the person turning it off would lose the
   * layout. The unflagged Signals page passes neither.
   */
  perDefinitionSave?: boolean;
  /**
   * A definition is open for editing.
   *
   * Reported so the page can hold a reload back: signals are one document, so
   * reloading while a row is open replaces the thing being edited underneath
   * the person editing it.
   */
  onEditingChange?: (editing: boolean) => void;
}

function SignalsGroupComponent({
  store,
  saveSignals,
  focus,
  compact,
  perDefinitionSave,
  onEditingChange,
}: SignalsGroupComponentProps) {
  const { signalsPage, fetchFormFields, formFields, isAllowedToEdit } = store;
  const { savingSignals, signals, isErrorOnForm } = signalsPage;
  const { customerFitSignals, signalsV2 } = signals;

  /**
   * The one definition open for editing, across every group.
   *
   * Held here rather than in each group because saving writes the WHOLE
   * signals document: a half-typed definition left open in one group would be
   * written by a Save pressed in another. With one slot for the page, the only
   * pending edit that can exist is the one whose Save button you are looking
   * at.
   */
  const [openRow, setOpenRow] = useState<{
    group: number;
    index: number;
  } | null>(null);
  /**
   * Whether the open row still matches how it was found. A ref, not state:
   * only `requestOpen` reads it and it must not schedule a render of its own
   * on every keystroke inside the row.
   */
  const rowDirty = React.useRef(false);
  /** Leaving was refused. Rendered by the group that owns the open row. */
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    onEditingChange?.(openRow !== null);
  }, [openRow !== null]);

  const releaseRow = () => {
    rowDirty.current = false;
    setBlocked(false);
    setOpenRow(null);
  };

  const editSession = (group: number): SignalEditSession => ({
    openIndex: openRow && openRow.group === group ? openRow.index : null,
    blocked: blocked && !!openRow && openRow.group === group,
    requestOpen: (index) => {
      if (rowDirty.current) {
        setBlocked(true);
        return;
      }
      rowDirty.current = false;
      setBlocked(false);
      setOpenRow(index === null ? null : { group, index });
    },
    setDirty: (dirty) => {
      rowDirty.current = dirty;
    },
    release: releaseRow,
    block: () => setBlocked(true),
  });

  const [signalsParsingError, setSignalsParsingError] = useState(false);
  // Controlled so a jump from the sample can open a group. Left uncontrolled the
  // Accordion would keep its own idea of what is open and ignore the focus.
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const displaySignals = action(() => {
    if (customerFitSignals && !signalsV2) {
      try {
        const signalGroups = parseYamlSignalsToUi(customerFitSignals);

        signals.signalsV2 = signalGroups;

        setSignalsParsingError(false);
      } catch (_e) {
        setSignalsParsingError(true);
      }
    }
  });

  useEffect(() => {
    const asyncActions = async () => {
      if (!formFields) {
        await fetchFormFields();
      }
      displaySignals();
    };

    asyncActions();
  }, []);

  useEffect(() => displaySignals(), [customerFitSignals]);

  useEffect(() => {
    if (!focus) return;
    setOpenGroup(`signal_group_${focus.group}`);
    // After the accordion has been told to open, not in the same frame: the row
    // does not exist to scroll to until it has.
    const timer = window.setTimeout(() => {
      document
        .getElementById(`signal_${focus.group}_${focus.rule}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focus?.group, focus?.rule, focus?.nonce]);

  if (signalsParsingError) {
    return (
      <>
        <Alert variant="danger">
          Error parsing signals through the UI. Falling back to advanced signals
          mode
        </Alert>
        <LegacySignals saveSignals={saveSignals} />
      </>
    );
  }

  if (!signalsV2) return null;

  return (
    <>
      <Accordion
        className={compact ? 'mb-3 sig-groups' : 'mb-3'}
        activeKey={openGroup}
        onSelect={(key) => setOpenGroup(key as string | null)}
      >
        {signalsV2.map((signalGroup, signalGroupIndex) => {
          return (
            <Accordion.Item
              eventKey={`signal_group_${signalGroupIndex}`}
              key={`signal_group_${signalGroupIndex}`}
            >
              <Accordion.Header>
                {/* A heading and a count, not a text field.

                    The name was a full size input sitting in the header, which
                    cost about sixty pixels per group before a single definition
                    was visible, and put an input inside a button, where a click
                    lands on the button. Expanding a group is the frequent action
                    and renaming one is rare, so the header carries the frequent
                    one and the field moves inside. */}
                {compact ? (
                  <>
                    <span className="sig-group-name">
                      {signalGroup.groupName || 'Unnamed group'}
                    </span>
                    <span className="sig-group-count">
                      {signalGroup.signals.length}
                      {signalGroup.signals.length === 1
                        ? ' definition'
                        : ' definitions'}
                    </span>
                  </>
                ) : (
                  <FormControl
                    className="d-inline-block w-25"
                    type="text"
                    disabled={!isAllowedToEdit || savingSignals}
                    value={signalGroup.groupName}
                    placeholder="Signal group name"
                    onChange={action((e) => {
                      signalsV2[signalGroupIndex].groupName = e.target.value;
                    })}
                  />
                )}
              </Accordion.Header>
              <Accordion.Body>
                {compact && (
                  <div className="sig-group-rename">
                    <label
                      className="sig-group-rename-label"
                      htmlFor={`group_name_${signalGroupIndex}`}
                    >
                      Group name
                    </label>
                    <FormControl
                      id={`group_name_${signalGroupIndex}`}
                      size="sm"
                      type="text"
                      disabled={!isAllowedToEdit || savingSignals}
                      value={signalGroup.groupName}
                      placeholder="Signal group name"
                      onChange={action((e) => {
                        signalsV2[signalGroupIndex].groupName = e.target.value;
                      })}
                    />
                  </div>
                )}
                <SignalsRules
                  signalGroupIndex={signalGroupIndex}
                  focusedRule={
                    focus && focus.group === signalGroupIndex
                      ? focus.rule
                      : null
                  }
                  compact={compact}
                  edit={
                    perDefinitionSave
                      ? editSession(signalGroupIndex)
                      : undefined
                  }
                  onCommit={perDefinitionSave ? saveSignals : undefined}
                />
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
      {checkPermissions(
        PROFILES_PERMISSIONS.ARCHITECT,
        <div className="mt-3 mb-4">
          <Button
            variant="primary"
            className="me-1"
            onClick={action(() => {
              const signalGroup = signalGroupPlaceHolder(formFields);
              signalsV2.push(signalGroup);
            })}
            /* Deliberately does not write: the new group arrives holding a
               placeholder definition whose only value is the empty string, and
               writing that would persist a half-formed signal. It lands when
               the first definition inside it is saved. */
            disabled={!isAllowedToEdit || savingSignals}
          >
            Add signal group
          </Button>
          {/* Each definition carries its own Save, so one button for the card
              would be a second way to write the same thing, with a different
              scope. The unflagged Signals page has no per-definition control
              and keeps this one. */}
          {!perDefinitionSave && (
            <Button
              variant="success"
              onClick={saveSignals}
              disabled={!isAllowedToEdit || savingSignals || isErrorOnForm}
            >
              {savingSignals ? 'Saving...' : 'Save & test'}
            </Button>
          )}
        </div>
      )}
    </>
  );
}

export default inject('store')(observer(SignalsGroupComponent));
